interface KeywordPayload {
  ruleId: string;
  ruleOrder: number;
  keyword: string;
  sourcePattern: string;
}

interface TrieNode {
  next: Map<string, number>;
  fail: number;
  outputs: KeywordPayload[];
}

export interface KeywordEntry {
  ruleId: string;
  ruleOrder: number;
  keyword: string;
  sourcePattern: string;
}

export interface KeywordCandidate {
  ruleId: string;
  ruleOrder: number;
  sourcePattern: string;
  start: number;
  end: number;
  text: string;
  normalizedMatch: string;
}

export class KeywordAutomaton {
  private readonly nodes: TrieNode[] = [{ next: new Map(), fail: 0, outputs: [] }];

  constructor(entries: KeywordEntry[]) {
    for (const entry of entries) {
      this.insert(entry);
    }
    this.buildFailureLinks();
  }

  search(input: string): KeywordCandidate[] {
    if (input.length === 0) {
      return [];
    }

    const results: KeywordCandidate[] = [];
    const haystack = input.toLocaleLowerCase();
    let state = 0;

    for (let i = 0; i < haystack.length; i += 1) {
      const char = haystack[i];

      while (state > 0 && !this.nodes[state].next.has(char)) {
        state = this.nodes[state].fail;
      }

      const nextState = this.nodes[state].next.get(char);
      if (nextState !== undefined) {
        state = nextState;
      }

      const outputs = this.nodes[state].outputs;
      if (outputs.length === 0) {
        continue;
      }

      for (const output of outputs) {
        const end = i + 1;
        const start = end - output.keyword.length;
        if (start < 0) {
          continue;
        }
        const raw = input.slice(start, end);
        results.push({
          ruleId: output.ruleId,
          ruleOrder: output.ruleOrder,
          sourcePattern: output.sourcePattern,
          start,
          end,
          text: raw,
          normalizedMatch: raw.toLocaleLowerCase()
        });
      }
    }

    return results;
  }

  private insert(entry: KeywordEntry): void {
    const normalized = entry.keyword.toLocaleLowerCase();
    if (!normalized.length) {
      return;
    }

    let currentNode = 0;
    for (const char of normalized) {
      let nextNode = this.nodes[currentNode].next.get(char);
      if (nextNode === undefined) {
        nextNode = this.nodes.length;
        this.nodes.push({ next: new Map(), fail: 0, outputs: [] });
        this.nodes[currentNode].next.set(char, nextNode);
      }
      currentNode = nextNode;
    }

    this.nodes[currentNode].outputs.push({
      ruleId: entry.ruleId,
      ruleOrder: entry.ruleOrder,
      keyword: normalized,
      sourcePattern: entry.sourcePattern
    });
  }

  private buildFailureLinks(): void {
    const queue: number[] = [];

    for (const nextNode of this.nodes[0].next.values()) {
      this.nodes[nextNode].fail = 0;
      queue.push(nextNode);
    }

    while (queue.length > 0) {
      const current = queue.shift();
      if (current === undefined) {
        continue;
      }

      for (const [char, nextNode] of this.nodes[current].next.entries()) {
        queue.push(nextNode);
        let fallback = this.nodes[current].fail;

        while (fallback > 0 && !this.nodes[fallback].next.has(char)) {
          fallback = this.nodes[fallback].fail;
        }

        const fallbackNode = this.nodes[fallback].next.get(char) ?? 0;
        this.nodes[nextNode].fail = fallbackNode;
        this.nodes[nextNode].outputs.push(...this.nodes[fallbackNode].outputs);
      }
    }
  }
}
